import React from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, RouteProps, useLocation } from 'react-router'
import { UserRole } from 'types/user'
import { isCurrentUserLoadingInProgress, selectCurrentUser } from 'store/users/users.selectors'
import { LoadingPlaceholder } from 'components/atoms/LoadingPlaceholder'
import { intendedOrderTypeStorageKey, returnAfterAuthStorageKey } from 'routes/url-constants'

const extractOrderTypeFromPath = (pathWithQueryAndHash: string): string | null => {
  try {
    // Use dummy origin so URLSearchParams can parse ?query even for relative paths
    const u = new URL(pathWithQueryAndHash, 'https://local.invalid')
    const v = u.searchParams.get('orderType')
    return v && v.trim() ? v.trim() : null
  } catch {
    return null
  }
}

export interface PrivateRouteProps extends RouteProps {
  authorizedUserRoles: UserRole[]
  redirectUnauthorizedTo: string
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ authorizedUserRoles, redirectUnauthorizedTo, ...routeProps }) => {
  const currentUser = useSelector(selectCurrentUser)
  const isLoadingInProgress = useSelector(isCurrentUserLoadingInProgress)
  const location = useLocation()

  if (isLoadingInProgress) {
    return <LoadingPlaceholder />
  }

  const isUserAllowed = currentUser !== null && (authorizedUserRoles.length ? authorizedUserRoles.includes(currentUser?.role) : true)

  if (!isUserAllowed) {
    const backUrl = `${location.pathname}${location.search}${location.hash}`
    try {
      sessionStorage.setItem(returnAfterAuthStorageKey, backUrl)
    } catch {
      // ignore storage errors (private mode / quota)
    }
    const intendedOrderType = extractOrderTypeFromPath(backUrl)
    if (intendedOrderType) {
      try {
        sessionStorage.setItem(intendedOrderTypeStorageKey, intendedOrderType)
      } catch {
        // ignore
      }
    }
    const separator = redirectUnauthorizedTo.includes('?') ? '&' : '?'
    return <Redirect to={`${redirectUnauthorizedTo}${separator}backUrl=${encodeURIComponent(backUrl)}`} />
  }

  return <Route {...routeProps} />
}
